// LODSelector
// Builds thresholds from the manifest's lods[] array. Each entry has
// { level, label, maxCameraDistance } where maxCameraDistance is the
// upper bound for that level (null on the coarsest = unbounded).
//
// Returns a numeric LOD index (0/1/2) so callers can do array lookups
// straight into libraryParts.
//
// Hysteresis: once we settle on a level, we let the camera drift 10%
// past the boundary before stepping back up. Stops thrash at thresholds.
//
// Optional override: pass an `overrides` array (length = LOD count) to
// the constructor. When present, the manifest's maxCameraDistance values
// are ignored and the override array is used instead. This lets a
// freshly-deployed viewer change LOD bands without requiring a re-export.
// Use Infinity for the last entry to make the coarsest LOD open-ended.
//
//   new LODSelector(manifest.lods, [15, 500, Infinity])
//   // → LOD 0 < 15ft, LOD 1 between 15 and 500ft, LOD 2 beyond 500ft

const HYSTERESIS = 1.1;

export default class LODSelector {
  constructor(lodsMeta, overrides = null) {
    if (!Array.isArray(lodsMeta) || lodsMeta.length === 0) {
      throw new Error('LODSelector: manifest lods[] is required');
    }
    const sorted = [...lodsMeta].sort((a, b) => a.level - b.level);

    if (Array.isArray(overrides) && overrides.length > 0) {
      // Validate override length matches LOD count, otherwise fall
      // back to manifest values for safety. (A length mismatch usually
      // means the override was authored against a different manifest
      // schema and would silently misroute LOD selection.)
      if (overrides.length !== sorted.length) {
        console.warn(
          `[LODSelector] overrides.length (${overrides.length}) ` +
          `!= manifest lods.length (${sorted.length}); ignoring overrides.`
        );
        this.maxDistances = sorted.map(x => x.maxCameraDistance ?? Infinity);
      } else {
        this.maxDistances = overrides.map(v => (v == null ? Infinity : v));
      }
    } else {
      this.maxDistances = sorted.map(x => x.maxCameraDistance ?? Infinity);
    }

    this.levelCount = sorted.length;
  }

  // distance: number, currentLevel: number|null
  // returns: integer in [0, levelCount-1]
  select(distance, currentLevel = null) {
    // Hysteresis: if we're on a coarser level, only drop to finer when
    // we're well inside the finer band.
    if (currentLevel !== null && currentLevel > 0) {
      const stickyBoundary = this.maxDistances[currentLevel - 1] * HYSTERESIS;
      if (distance < stickyBoundary) return currentLevel - 1;
    }
    for (let i = 0; i < this.levelCount; i++) {
      if (distance < this.maxDistances[i]) return i;
    }
    return this.levelCount - 1;
  }
}